import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
    IconEdit as FiEdit2,
    IconTrash as FiTrash2,
    IconPlus as FiPlus,
    IconX as FiX,
    IconSearch,
    IconRefresh,
    IconLayoutGrid,
    IconList,
    IconCheck,
    IconLayoutDashboard,
    IconVideo,
    IconBook,
    IconNews,
    IconExternalLink,
    IconUpload,
    IconChevronRight,
    IconSettings,
    IconLogout
} from "@tabler/icons-react";

import { API_URL, formatFileUrl } from "../../utils/api";

const API_BASE = `${API_URL}/library`;

function extractVideoId(url) {
    if (!url) return null;
    const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
}

export const AdminLibrary = () => {
    const handleLogout = () => {
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_user");
        window.location.reload();
    };

    const [activeTab, setActiveTab] = useState("all");

    const [categories, setCategories] = useState([]);
    const [allItems, setAllItems] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [items, setItems] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [colSearchTerm, setColSearchTerm] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [notification, setNotification] = useState(null);

    // Modals
    const [isCatModalOpen, setCatModalOpen] = useState(false);
    const [isItemModalOpen, setItemModalOpen] = useState(false);

    // Forms
    const [catForm, setCatForm] = useState({ title: "", subtitle: "", coverImage: "", mainUrl: "", type: "book" });
    const [itemForm, setItemForm] = useState({ title: "", day: "", url: "", pdf: "", coverImage: "", month: "" });
    const [editCatId, setEditCatId] = useState(null);
    const [editItemId, setEditItemId] = useState(null);

    // Confirmation Modal State
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, text: "", onConfirm: null });

    const showNotification = (msg, type = "success") => {
        setNotification({ msg, type });
        setTimeout(() => setNotification(null), 3000);
    };

    const fetchCategories = async (type) => {
        setIsRefreshing(true);
        try {
            const url = type === "all" ? `${API_URL}/library/categories` : `${API_URL}/library/categories?type=${type}`;
            const res = await axios.get(url);
            setCategories(Array.isArray(res.data) ? res.data : []);

            if (type === "all") {
                const itemsRes = await axios.get(`${API_URL}/library/items`);
                setAllItems(Array.isArray(itemsRes.data) ? itemsRes.data : []);
            }
        } catch (err) {
            console.error(err);
            showNotification("Failed to fetch data", "error");
        } finally {
            setIsRefreshing(false);
        }
    };

    const fetchItems = useCallback(async (catId) => {
        try {
            const res = await axios.get(`${API_BASE}/items?category=${catId}`);
            setItems(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error(err);
            showNotification("Failed to fetch items", "error");
        }
    }, []);

    useEffect(() => {
        const interceptor = axios.interceptors.request.use(config => {
            const token = localStorage.getItem("admin_token");
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        });

        return () => {
            axios.interceptors.request.eject(interceptor);
        };
    }, []);


    useEffect(() => {
        fetchCategories(activeTab);

        setSelectedCategory(null);
        setItems([]);
    }, [activeTab]);

    useEffect(() => {
        if (selectedCategory) {
            fetchItems(selectedCategory._id);
        }
    }, [selectedCategory, fetchItems]);

    const handleUpload = async (e, field, isItem) => {
        const file = e.target.files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append("file", file);
        try {
            showNotification("Uploading...", "info");
            const res = await axios.post(`${API_BASE}/upload`, formData, {
                headers: { "Content-Type": "multipart/form-data" }
            });
            if (isItem) {
                setItemForm(prev => ({ ...prev, [field]: res.data.url }));
            } else {
                setCatForm(prev => ({ ...prev, [field]: res.data.url }));
            }
            showNotification("File uploaded successfully");
        } catch (err) {
            console.error(err);
            showNotification("Upload failed", "error");
        }
    };

    const saveCategory = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const finalType = editCatId ? catForm.type : (activeTab === "all" ? catForm.type : activeTab);
            if (editCatId) {
                await axios.put(`${API_BASE}/categories/${editCatId}`, { ...catForm, type: finalType });
                showNotification("Collection updated");
            } else {
                await axios.post(`${API_BASE}/categories`, { ...catForm, type: finalType });
                showNotification("Collection created");
            }
            setCatModalOpen(false);
            setEditCatId(null);
            fetchCategories(activeTab);
        } catch (err) {
            console.error(err);
            showNotification("Failed to save collection", "error");
        } finally {
            setIsSaving(false);
        }
    };

    const deleteCategory = (id) => {
        setConfirmModal({
            isOpen: true,
            text: "Delete this collection? All resources inside will be permanently removed.",
            onConfirm: async () => {
                setConfirmModal({ isOpen: false, text: "", onConfirm: null });
                try {
                    await axios.delete(`${API_BASE}/categories/${id}`);
                    if (selectedCategory?._id === id) setSelectedCategory(null);
                    fetchCategories(activeTab);
                    showNotification("Collection removed");
                } catch (err) {
                    console.error(err);
                    showNotification("Error deleting collection", "error");
                }
            }
        });
    };

    const saveItem = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            if (editItemId) {
                await axios.put(`${API_BASE}/items/${editItemId}`, itemForm);
                showNotification("Resource updated");
            } else {
                await axios.post(`${API_BASE}/items`, { ...itemForm, category: selectedCategory._id });
                showNotification("Resource created");
            }
            setItemModalOpen(false);
            setEditItemId(null);
            fetchItems(selectedCategory._id);
        } catch (err) {
            console.error(err);
            showNotification("Failed to save resource", "error");
        } finally {
            setIsSaving(false);
        }
    };

    const deleteItem = (id) => {
        setConfirmModal({
            isOpen: true,
            text: "Are you sure you want to delete this resource?",
            onConfirm: async () => {
                setConfirmModal({ isOpen: false, text: "", onConfirm: null });
                try {
                    await axios.delete(`${API_BASE}/items/${id}`);
                    fetchItems(selectedCategory._id);
                    showNotification("Resource removed");
                } catch (err) {
                    console.error(err);
                    showNotification("Error removing resource", "error");
                }
            }
        });
    };

    const openCatEdit = (cat) => {
        setCatForm(cat);
        setEditCatId(cat._id);
        setCatModalOpen(true);
    };

    const openCatNew = () => {
        setCatForm({ title: "", subtitle: "", coverImage: "", mainUrl: "", type: activeTab === 'all' ? 'book' : activeTab });
        setEditCatId(null);
        setCatModalOpen(true);
    };

    const openItemEdit = (item) => {
        setItemForm(item);
        setEditItemId(item._id);
        setItemModalOpen(true);
    };

    const openItemNew = () => {
        setItemForm({ title: "", day: "", url: "", pdf: "", coverImage: "", month: "" });
        setEditItemId(null);
        setItemModalOpen(true);
    };

    const getItemImage = (item) => {
        if (item.coverImage) {
            return formatFileUrl(item.coverImage);
        }

        // Find category to check type
        const cat = categories.find(c => c._id === item.category) || selectedCategory;
        if (item.url || (cat && cat.type === 'video')) {
            const vidId = extractVideoId(item.url || (cat && cat.mainUrl));
            if (vidId) return `https://img.youtube.com/vi/${vidId}/mqdefault.jpg`;
        }

        return "https://tipyaanacademy.com/wp-content/uploads/2023/07/muslim-school.jpg";
    };

    const getCategoryImage = (cat) => {
        if (cat.coverImage) return formatFileUrl(cat.coverImage);
        if (cat.type === 'video' && cat.mainUrl) {
            const vidId = extractVideoId(cat.mainUrl);
            if (vidId) return `https://img.youtube.com/vi/${vidId}/mqdefault.jpg`;
        }
        return "https://tipyaanacademy.com/wp-content/uploads/2023/07/muslim-school.jpg";
    };

    const filteredItems = items.filter(item => {
        const query = searchTerm.toLowerCase();
        return (item.title || "").toLowerCase().includes(query) ||
               (item.day || "").toLowerCase().includes(query) ||
               (item.month || "").toLowerCase().includes(query);
    });

    const filteredCategories = categories.filter(cat => {
        const query = colSearchTerm.toLowerCase();
        return (cat.title || "").toLowerCase().includes(query) ||
               (cat.subtitle || "").toLowerCase().includes(query);
    });

    return (
        <div className="min-h-screen bg-[#FDFDFD] pt-8 pb-12 px-4 md:px-8 font-sans selection:bg-pumpkinToast-100 selection:text-pumpkinToast-900 max-w-full overflow-x-hidden">
            {/* Notification Bar */}
            {notification && (
                <div className={`fixed top-8 left-1/2 -translate-x-1/2 z-[200] px-6 py-3 rounded-2xl shadow-2xl flex items-center space-x-3 transition-all transform animate-in fade-in slide-in-from-top-4 ${notification.type === "success" ? "bg-neutral-900 text-white" :
                    notification.type === "error" ? "bg-red-600 text-white" : "bg-blue-600 text-white"
                    }`}>
                    <IconCheck size={18} className={notification.type === "success" ? "text-pumpkinToast-300" : "text-white"} />
                    <span className="text-sm font-medium tracking-tight">{notification.msg}</span>
                </div>
            )}

            <div className="max-w-7xl mx-auto">
                {/* Modern Header */}
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <span className="px-2 py-0.5 bg-pumpkinToast-50 text-pumpkinToast-600 text-[10px] font-bold uppercase rounded-md tracking-wider border border-pumpkinToast-100">
                                Admin Dashboard
                            </span>
                        </div>
                        <h1 className="text-2xl sm:text-3xl font-medium text-neutral-900 tracking-tight">
                            Library <span className="text-neutral-400">Management</span>
                        </h1>
                        <p className="text-sm text-neutral-500 mt-2 max-w-md">Create, organize and publish your digital assets with a clean and efficient workspace.</p>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        <button
                            onClick={handleLogout}
                            className="flex items-center space-x-2 px-6 py-3 bg-red-50 text-red-600 rounded-2xl font-medium border border-red-100 hover:bg-red-100 transition-all shadow-sm"
                        >
                            <span className="text-sm">Logout</span>
                            <IconLogout size={16} />
                        </button>
                        <a
                            href="/library"

                            target="_blank"
                            rel="noreferrer"
                            className="group flex items-center space-x-2 px-6 py-3 bg-black text-white rounded-2xl font-medium border border-neutral-200 hover:bg-neutral-800 transition-all shadow-sm"
                        >
                            <span className="text-sm">Visit Library</span>
                            <IconExternalLink size={16} className="text-neutral-400 group-hover:text-neutral-200 transition-colors" />
                        </a>
                        <button
                            onClick={() => fetchCategories(activeTab)}
                            disabled={isRefreshing}
                            className={`p-3 rounded-2xl bg-white border border-neutral-200 text-neutral-500 hover:border-neutral-900 hover:text-neutral-900 transition-all shadow-sm ${isRefreshing ? "opacity-50" : ""}`}
                        >
                            <IconRefresh size={20} className={isRefreshing ? "animate-spin" : ""} />
                        </button>
                    </div>
                </div>

                {/* Tabbed Navigation */}
                <div className="flex overflow-x-auto items-center gap-2 mb-8 bg-neutral-100/60 p-1.5 rounded-[22px] w-fit max-w-full scrollbar-hide border border-neutral-200/50">
                    {[
                        { id: 'all', label: 'Dashboard', icon: <IconLayoutDashboard size={18} /> },
                        { id: 'video', label: 'Playlists', icon: <IconVideo size={18} /> },
                        { id: 'book', label: 'Books', icon: <IconBook size={18} /> },
                        { id: 'magazine', label: 'Magazines', icon: <IconNews size={18} /> },
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`px-6 py-2.5 rounded-2xl font-medium transition-all flex items-center space-x-2 text-sm whitespace-nowrap ${activeTab === tab.id
                                ? "bg-white text-neutral-900 shadow-md ring-1 ring-black/5"
                                : "text-neutral-500 hover:text-neutral-800 hover:bg-white/50"
                                }`}
                        >
                            {tab.icon}
                            <span>{tab.label}</span>
                        </button>
                    ))}
                </div>

                {/* Main Workspace */}
                <div className="min-h-[600px]">
                    {activeTab === "all" ? (
                        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-700">
                            {/* Stats Grid */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                                {[
                                    { label: 'Total Content', value: allItems.length, icon: <IconLayoutGrid size={24} />, color: 'text-neutral-900 bg-neutral-100' },
                                    { label: 'Active Categories', value: categories.length, icon: <IconSettings size={24} />, color: 'text-pumpkinToast-600 bg-pumpkinToast-50' },
                                    { label: 'Video Lessons', value: allItems.filter(i => (i.category?.type || categories.find(c => c._id === (i.category?._id || i.category))?.type) === 'video').length, icon: <IconVideo size={24} />, color: 'text-blue-600 bg-blue-50' },
                                    { label: 'Printed Media', value: allItems.filter(i => (i.category?.type || categories.find(c => c._id === (i.category?._id || i.category))?.type) !== 'video').length, icon: <IconBook size={24} />, color: 'text-green-600 bg-green-100' },
                                ].map((stat, idx) => (
                                    <div key={idx} className="bg-white p-6 rounded-3xl border border-neutral-200/80 shadow-sm hover:shadow-md transition-all group overflow-hidden relative">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className={`p-3 rounded-2xl ${stat.color} transition-transform group-hover:rotate-12`}>
                                                {stat.icon}
                                            </div>
                                            <IconChevronRight size={16} className="text-neutral-300 group-hover:text-neutral-400 group-hover:translate-x-1 transition-all" />
                                        </div>
                                        <div>
                                            <p className="text-[11px] uppercase font-semibold text-neutral-400 tracking-wider mb-1">{stat.label}</p>
                                            <p className="text-3xl font-medium text-neutral-900">{stat.value}</p>
                                        </div>
                                        <div className="absolute -right-4 -bottom-4 w-24 h-24 bg-neutral-50 rounded-full opacity-50 transition-all group-hover:scale-110"></div>
                                    </div>
                                ))}
                            </div>

                            {/* Global Registry Table */}
                            <div className="bg-white rounded-3xl border border-neutral-200/80 shadow-sm overflow-hidden">
                                <div className="p-6 border-b border-neutral-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-neutral-950 text-white rounded-xl flex items-center justify-center">
                                            <IconList size={20} />
                                        </div>
                                        <h3 className="font-medium text-neutral-800 text-lg">Master Inventory</h3>
                                    </div>
                                    <div className="relative">
                                        <IconSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" size={18} />
                                        <input
                                            type="text"
                                            placeholder="Search items by title..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="pl-11 pr-5 py-3 bg-neutral-50 rounded-2xl border border-neutral-100 text-sm w-full sm:w-80 focus:bg-white focus:border-neutral-900 outline-none transition-all shadow-inner"
                                        />
                                    </div>
                                </div>

                                <div className="p-2">
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                        {allItems.filter(i => {
                                            const q = searchQuery.toLowerCase();
                                            const catTitle = (i.category?.title || categories.find(c => c._id === (i.category?._id || i.category))?.title || "").toLowerCase();
                                            return (i.title || "").toLowerCase().includes(q) ||
                                                   catTitle.includes(q) ||
                                                   (i.day || "").toLowerCase().includes(q) ||
                                                   (i.month || "").toLowerCase().includes(q);
                                        }).slice(0, 60).map((item) => {
                                            const cat = item.category?.title ? item.category : categories.find(c => c._id === (item.category?._id || item.category));
                                            return (
                                                <div key={item._id} className="flex items-center gap-4 p-4 rounded-2xl hover:bg-neutral-50 transition-all group">
                                                    <div className="w-14 h-14 rounded-[20px] overflow-hidden bg-neutral-100 flex-shrink-0 border border-neutral-200/50 shadow-sm">
                                                        <img
                                                            src={getItemImage(item)}
                                                            alt=""
                                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                                            onError={(e) => e.target.src = "https://tipyaanacademy.com/wp-content/uploads/2023/07/muslim-school.jpg"}
                                                        />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-neutral-900 truncate mb-0.5">{item.title}</p>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-[10px] px-2 py-0.5 bg-neutral-100 text-neutral-500 rounded-lg border border-neutral-200 group-hover:bg-neutral-200 transition-colors uppercase tracking-tight">
                                                                {cat?.title || "Draft"}
                                                            </span>
                                                            <span className={`w-1.5 h-1.5 rounded-full ${cat?.type === 'video' ? 'bg-blue-400' : 'bg-green-400'}`}></span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {allItems.length === 0 && (
                                            <div className="col-span-full py-32 text-center">
                                                <div className="w-20 h-20 bg-neutral-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-dashed border-neutral-200">
                                                    <IconLayoutGrid size={32} className="text-neutral-200" />
                                                </div>
                                                <p className="text-neutral-400 font-medium">Your library is empty. Select a tab to start adding content.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
                            {/* Collection Side-Bar */}
                            <div className="lg:col-span-4 space-y-4">
                                <div className="bg-white rounded-[32px] border border-neutral-200/80 overflow-hidden flex flex-col h-[400px] lg:h-[650px] shadow-sm">
                                    <div className="p-6 border-b border-neutral-100 flex flex-col gap-4 bg-white">
                                        <div className="flex justify-between items-center">
                                            <div>
                                                <h2 className="text-sm font-semibold text-neutral-900 uppercase tracking-[0.1em] opacity-50">Collections</h2>
                                                <p className="text-[10px] text-neutral-400 mt-0.5">Categorize your {activeTab}s</p>
                                            </div>
                                            {!(activeTab === "magazine" && categories.length > 0) && (
                                                <button
                                                    onClick={openCatNew}
                                                    className="w-10 h-10 bg-neutral-900 text-black rounded-2xl hover:bg-neutral-800 transition-all shadow-md active:scale-95 flex items-center justify-center"
                                                    title="New Collection"
                                                >
                                                    <FiPlus size={20} />
                                                </button>
                                            )}
                                        </div>
                                        <div className="relative">
                                            <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={14} />
                                            <input
                                                type="text"
                                                placeholder="Search collections..."
                                                value={colSearchTerm}
                                                onChange={(e) => setColSearchTerm(e.target.value)}
                                                className="w-full pl-9 pr-3 py-2 bg-neutral-50 rounded-xl border border-neutral-100 text-xs focus:bg-white focus:border-neutral-900 outline-none transition-all shadow-inner"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-hide">
                                        {filteredCategories.map((cat) => (
                                            <div
                                                key={cat._id}
                                                className={`group relative p-4 rounded-3xl border transition-all cursor-pointer ${selectedCategory?._id === cat._id
                                                    ? "border-neutral-950 bg-neutral-50 shadow-inner"
                                                    : "border-neutral-100 bg-white hover:border-neutral-300 hover:shadow-md"
                                                    }`}
                                                onClick={() => setSelectedCategory(cat)}
                                            >
                                                <div className="flex justify-between items-center gap-4">
                                                    <div className="w-14 h-14 rounded-2xl overflow-hidden bg-neutral-50 flex-shrink-0 border border-neutral-200/50 shadow-sm relative">
                                                        <img src={getCategoryImage(cat)} alt="" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" onError={(e) => e.target.src = "https://tipyaanacademy.com/wp-content/uploads/2023/07/muslim-school.jpg"} />
                                                        {selectedCategory?._id === cat._id && (
                                                            <div className="absolute inset-0 bg-neutral-900/10 backdrop-blur-[1px] flex items-center justify-center">
                                                                <IconCheck size={20} className="text-white drop-shadow-md" />
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <h3 className="text-sm font-medium truncate text-neutral-900 mb-0.5 uppercase tracking-tight">
                                                            {cat.title}
                                                        </h3>
                                                        <p className="text-[10px] text-neutral-400 flex items-center gap-1.5 font-medium">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-neutral-300"></span>
                                                            {cat.subtitle || "No subtitle"}
                                                        </p>
                                                    </div>
                                                    <div className="flex items-center gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                                                        <button onClick={(e) => { e.stopPropagation(); openCatEdit(cat); }} className="p-2 text-neutral-400 hover:text-neutral-900 transition-colors rounded-xl hover:bg-white"><FiEdit2 size={16} /></button>
                                                        <button onClick={(e) => { e.stopPropagation(); deleteCategory(cat._id); }} className="p-2 text-neutral-400 hover:text-red-500 transition-colors rounded-xl hover:bg-white"><FiTrash2 size={16} /></button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                        {filteredCategories.length === 0 && (
                                            <div className="py-32 text-center">
                                                <div className="w-16 h-16 bg-neutral-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-dashed border-neutral-200">
                                                    <IconLayoutGrid size={24} className="text-neutral-300" />
                                                </div>
                                                <p className="text-xs text-neutral-400 font-medium">No collections yet</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Resource Explorer */}
                            <div className="lg:col-span-8">
                                <div className="bg-white rounded-[32px] border border-neutral-200/80 flex flex-col h-[500px] lg:h-[650px] shadow-sm relative overflow-hidden">
                                    {!selectedCategory ? (
                                        <div className="h-full flex flex-col items-center justify-center text-neutral-400 p-10 text-center bg-[#FAFAFA]">
                                            <div className="w-24 h-24 bg-white rounded-full shadow-lg flex items-center justify-center mb-6 ring-8 ring-neutral-50">
                                                <IconList size={36} className="text-neutral-900" />
                                            </div>
                                            <h3 className="text-neutral-900 font-medium text-xl mb-2">Explorer is ready</h3>
                                            <p className="text-sm max-w-[280px] leading-relaxed">Select a collection from the sidebar to start managing your resources and content.</p>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col h-full">
                                            <div className="p-6 border-b border-neutral-100 flex flex-col sm:flex-row sm:items-center justify-between gap-6 bg-white z-10">
                                                <div>
                                                    <h2 className="text-xl font-medium text-neutral-950 uppercase tracking-tight">
                                                        {selectedCategory.title}
                                                    </h2>
                                                    <p className="text-[10px] text-neutral-400 font-bold uppercase tracking-[0.2em] mt-1">Resource Content</p>
                                                </div>

                                                <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full sm:w-auto">
                                                    <div className="relative w-full sm:w-auto">
                                                        <IconSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" size={16} />
                                                        <input
                                                            type="text"
                                                            placeholder="Quick search items..."
                                                            value={searchTerm}
                                                            onChange={(e) => setSearchTerm(e.target.value)}
                                                            className="pl-11 pr-5 py-2.5 bg-neutral-50 border border-neutral-100 rounded-2xl text-[13px] outline-none focus:bg-white focus:border-neutral-900 transition-all w-full sm:w-60 shadow-inner"
                                                        />
                                                    </div>
                                                    <button
                                                        onClick={openItemNew}
                                                        className="flex items-center justify-center gap-2 px-6 py-3 bg-neutral-900 text-black rounded-2xl hover:bg-neutral-800 transition-all shadow-md active:scale-95 group font-medium text-sm w-full sm:w-auto"
                                                    >
                                                        <FiPlus size={18} className="group-hover:rotate-90 transition-transform" />
                                                        <span>Add Resource</span>
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="flex-1 overflow-y-auto p-6 scrollbar-hide bg-[#FAFAFA]/50">
                                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                                    {filteredItems.map((item) => {
                                                        const isPortrait = activeTab === 'book' || activeTab === 'magazine';
                                                        return (
                                                            <div key={item._id} className="flex flex-col bg-white rounded-3xl border border-neutral-200/50 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group ring-4 ring-transparent hover:ring-neutral-950/5">
                                                                <div className={`relative ${isPortrait ? 'aspect-[3/4]' : 'aspect-video'} bg-neutral-100`}>
                                                                    <img
                                                                        src={getItemImage(item)}
                                                                        alt=""
                                                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                                                        onError={(e) => { e.target.src = "https://tipyaanacademy.com/wp-content/uploads/2023/07/muslim-school.jpg"; e.target.className = "w-full h-full object-contain p-4 opacity-50"; }}
                                                                    />
                                                                    <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/60 to-transparent translate-y-0 md:translate-y-2 opacity-100 md:opacity-0 md:group-hover:translate-y-0 md:group-hover:opacity-100 transition-all">
                                                                        <div className="flex gap-2">
                                                                            <button onClick={() => openItemEdit(item)} className="flex-1 py-1.5 bg-white backdrop-blur-md rounded-xl text-neutral-900 text-[11px] font-bold shadow hover:bg-white transition-colors uppercase tracking-wider">Edit</button>
                                                                            <button onClick={() => deleteItem(item._id)} className="px-3 py-1.5 bg-red-500 rounded-xl text-white shadow hover:bg-red-600 transition-colors"><FiTrash2 size={14} /></button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="p-4 bg-white">
                                                                    <h3 className="text-sm font-medium text-neutral-900 line-clamp-2 min-h-[40px] leading-snug">{item.title}</h3>
                                                                    <div className="flex flex-wrap gap-2 mt-3">
                                                                        {item.pdf && <span className="text-[9px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-lg border border-blue-100 uppercase tracking-tighter shadow-sm">PDF Reader</span>}
                                                                        {item.url && <span className="text-[9px] font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-lg border border-red-100 uppercase tracking-tighter shadow-sm">Video Lesson</span>}
                                                                        {item.day && <span className="text-[9px] font-bold text-neutral-500 bg-neutral-100 px-2 py-0.5 rounded-lg border border-neutral-200 uppercase tracking-tighter shadow-sm">{item.day}</span>}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                    {filteredItems.length === 0 && (
                                                        <div className="col-span-full py-40 text-center">
                                                            <div className="w-20 h-20 bg-neutral-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-dashed border-neutral-200">
                                                                <IconSearch size={28} className="text-neutral-300" />
                                                            </div>
                                                            <p className="text-sm text-neutral-400 font-medium">No matching items found in this collection.</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Collection Modal */}
            {isCatModalOpen && (
                <div className="fixed inset-0 bg-neutral-950/40 backdrop-blur-md flex items-center justify-center z-[300] p-6 animate-in fade-in duration-300">
                    <div className="bg-white rounded-[40px] p-8 w-full max-w-lg shadow-2xl relative overflow-hidden border border-white/20">
                        <div className="flex justify-between items-center mb-8">
                            <div>
                                <h2 className="text-2xl font-medium text-neutral-900 tracking-tight">
                                    {editCatId ? "Collection Settings" : "Build New Collection"}
                                </h2>
                                <p className="text-[10px] text-neutral-400 font-bold uppercase tracking-widest mt-1">Configure asset grouping</p>
                            </div>
                            <button onClick={() => setCatModalOpen(false)} className="w-10 h-10 bg-neutral-50 rounded-2xl flex items-center justify-center text-neutral-400 hover:text-neutral-900 transition-all active:scale-95">
                                <FiX size={20} />
                            </button>
                        </div>

                        <form onSubmit={saveCategory} className="space-y-6">
                            {activeTab === "all" && !editCatId && (
                                <div>
                                    <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">Group Type</label>
                                    <div className="flex gap-2 mt-2">
                                        {['video', 'book', 'magazine'].map(t => (
                                            <button
                                                key={t}
                                                type="button"
                                                onClick={() => setCatForm({ ...catForm, type: t })}
                                                className={`flex-1 py-3 rounded-2xl text-[10px] font-bold uppercase tracking-wider transition-all border ${catForm.type === t ? 'bg-neutral-900 text-white border-neutral-900 shadow-md' : 'bg-neutral-50 text-neutral-500 border-neutral-100 hover:border-neutral-300'}`}
                                            >
                                                {t}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div>
                                <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">Display Title</label>
                                <input required type="text" value={catForm.title} onChange={(e) => setCatForm({ ...catForm, title: e.target.value })} className="w-full mt-2 px-5 py-3 bg-neutral-50 rounded-2xl border border-neutral-100 outline-none text-sm focus:border-neutral-900 transition-all font-medium placeholder:text-neutral-300" placeholder="e.g. Modern Architecture" />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">Short Description</label>
                                    <input type="text" value={catForm.subtitle} onChange={(e) => setCatForm({ ...catForm, subtitle: e.target.value })} className="w-full mt-2 px-5 py-3 bg-neutral-50 rounded-2xl border border-neutral-100 text-xs focus:border-neutral-900 outline-none" placeholder="e.g. 2024 Collection" />
                                </div>
                            </div>

                            {(activeTab === "video" || catForm.type === 'video') && (
                                <div>
                                    <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">External Playlist Link (YouTube)</label>
                                    <input type="text" value={catForm.mainUrl} onChange={(e) => setCatForm({ ...catForm, mainUrl: e.target.value })} className="w-full mt-2 px-5 py-3 bg-neutral-50 rounded-2xl border border-neutral-100 text-xs focus:border-neutral-900 outline-none" placeholder="https://youtube.com/..." />
                                </div>
                            )}

                            <div>
                                <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">Visual Cover Path</label>
                                <div className="space-y-4">
                                    <div className="flex gap-3">
                                        <input type="text" value={catForm.coverImage} onChange={(e) => setCatForm({ ...catForm, coverImage: e.target.value })} className="flex-1 px-5 py-3 bg-neutral-50 rounded-2xl border border-neutral-100 text-xs focus:border-neutral-900 outline-none" placeholder="Enter path or upload..." />
                                        <label className="p-3 bg-neutral-900 text-black rounded-2xl cursor-pointer hover:bg-neutral-800 transition-all shadow-md active:scale-95">
                                            <IconUpload size={20} />
                                            <input type="file" className="hidden" onChange={(e) => handleUpload(e, "coverImage", false)} />
                                        </label>
                                    </div>
                                    {catForm.coverImage && (
                                        <div className="w-full h-32 rounded-3xl bg-neutral-100 overflow-hidden border border-neutral-200 relative group">
                                            <img src={formatFileUrl(catForm.coverImage)} alt="Preview" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                                            <div className="absolute inset-0 bg-black/10"></div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <button disabled={isSaving} type="submit" className="w-full bg-neutral-950 text-black py-4 rounded-[22px] font-bold text-[11px] uppercase tracking-[0.2em] shadow-2xl shadow-neutral-500/20 hover:bg-neutral-800 transition-all active:scale-98 disabled:opacity-50">
                                {isSaving ? "Syncing Workspace..." : (editCatId ? "Commit Changes" : "Establish Collection")}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Item Modal */}
            {isItemModalOpen && (
                <div className="fixed inset-0 bg-neutral-950/40 backdrop-blur-md flex items-center justify-center z-[300] p-6 animate-in fade-in duration-300">
                    <div className="bg-white rounded-[40px] p-8 w-full max-w-lg shadow-2xl relative max-h-[92vh] overflow-y-auto scrollbar-hide border border-white/20">
                        <div className="flex justify-between items-center mb-8 sticky top-0 bg-white/10 backdrop-blur-xl -mx-4 -mt-4 p-4 z-10 border-b border-neutral-100 rounded-t-[32px]">
                            <div>
                                <h2 className="text-2xl font-medium text-neutral-900 tracking-tight">
                                    {editItemId ? "Refine Resource" : "Inject New Resource"}
                                </h2>
                                <p className="text-[10px] text-neutral-400 font-bold uppercase tracking-widest mt-1">Resource Meta Data</p>
                            </div>
                            <button onClick={() => setItemModalOpen(false)} className="w-10 h-10 bg-neutral-50 rounded-2xl flex items-center justify-center text-neutral-400 hover:text-neutral-900 transition-all">
                                <FiX size={20} />
                            </button>
                        </div>

                        <form onSubmit={saveItem} className="space-y-6 pt-2">
                            <div>
                                <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">Resource Title</label>
                                <input required type="text" value={itemForm.title} onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })} className="w-full mt-2 px-5 py-3 bg-neutral-50 rounded-2xl border border-neutral-100 outline-none text-sm focus:border-neutral-900 transition-all font-medium placeholder:text-neutral-300" placeholder="Proper name of the asset..." />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {activeTab === "video" ? (
                                    <>
                                        <div>
                                            <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">Index / Day</label>
                                            <input type="text" value={itemForm.day} onChange={(e) => setItemForm({ ...itemForm, day: e.target.value })} className="w-full mt-2 px-5 py-3 bg-neutral-50 rounded-2xl border border-neutral-100 text-xs outline-none focus:border-neutral-900" placeholder="e.g. Day 01" />
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">YouTube Index</label>
                                            <input type="text" value={itemForm.url} onChange={(e) => setItemForm({ ...itemForm, url: e.target.value })} className="w-full mt-2 px-5 py-3 bg-neutral-50 rounded-2xl border border-neutral-100 text-xs outline-none focus:border-neutral-900" placeholder="Full link or ID" />
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div>
                                            <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">Meta Label</label>
                                            <input type="text" value={itemForm.month} onChange={(e) => setItemForm({ ...itemForm, month: e.target.value })} className="w-full mt-2 px-5 py-3 bg-neutral-50 rounded-2xl border border-neutral-100 text-xs focus:border-neutral-900 outline-none" placeholder="e.g. Jan Issue / Edition" />
                                        </div>
                                        <div className="flex flex-col">
                                            <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">Document (PDF)</label>
                                            <div className="flex gap-2 mt-2">
                                                <input type="text" value={itemForm.pdf} onChange={(e) => setItemForm({ ...itemForm, pdf: e.target.value })} className="flex-1 px-4 py-3 bg-neutral-50 rounded-2xl border border-neutral-100 text-[10px] focus:border-neutral-900 outline-none" placeholder="Cloud path..." />
                                                <label className="p-3 bg-neutral-50 text-neutral-900 rounded-2xl cursor-pointer hover:bg-neutral-100 border border-neutral-200 transition-all flex items-center shadow-sm">
                                                    <IconUpload size={18} />
                                                    <input type="file" className="hidden" onChange={(e) => handleUpload(e, "pdf", true)} />
                                                </label>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>

                            <div>
                                <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">Thumbnail Source</label>
                                <div className="space-y-4">
                                    <div className="flex gap-3 mt-1">
                                        <input type="text" value={itemForm.coverImage} onChange={(e) => setItemForm({ ...itemForm, coverImage: e.target.value })} className="flex-1 px-5 py-3 bg-neutral-50 rounded-2xl border border-neutral-100 text-xs outline-none focus:border-neutral-900" placeholder="Leave empty for auto-generated..." />
                                        <label className="p-3 bg-neutral-900 text-black rounded-2xl cursor-pointer hover:bg-neutral-800 transition-all shadow-md active:scale-95">
                                            <IconUpload size={18} />
                                            <input type="file" className="hidden" onChange={(e) => handleUpload(e, "coverImage", true)} />
                                        </label>
                                    </div>
                                    {itemForm.coverImage && (
                                        <div className="w-full h-40 rounded-3xl bg-neutral-100 overflow-hidden border border-neutral-200 relative group">
                                            <img src={formatFileUrl(itemForm.coverImage)} alt="Preview" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <button disabled={isSaving} type="submit" className="w-full bg-neutral-950 text-black py-4 rounded-[22px] font-bold text-[11px] uppercase tracking-[0.2em] shadow-2xl shadow-neutral-900/20 hover:bg-neutral-800 transition-all active:scale-98 disabled:opacity-50">
                                {isSaving ? "Syncing Asset..." : (editItemId ? "Publish Updates" : "Commit Resource")}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Confirmation Modal */}
            {confirmModal.isOpen && (
                <div className="fixed inset-0 bg-neutral-950/40 backdrop-blur-md flex items-center justify-center z-[400] p-6 animate-in fade-in duration-200">
                    <div className="bg-white rounded-[32px] p-8 w-full max-w-sm shadow-2xl relative border border-white/20 text-center animate-in zoom-in-95 duration-200">
                        <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4 border border-red-100">
                            <FiTrash2 size={24} />
                        </div>
                        <h3 className="text-xl font-medium text-neutral-900 mb-2">Confirm Deletion</h3>
                        <p className="text-sm text-neutral-500 mb-8">{confirmModal.text}</p>
                        <div className="flex gap-3">
                            <button onClick={() => setConfirmModal({ isOpen: false, text: "", onConfirm: null })} className="flex-1 py-3 bg-neutral-100 text-neutral-600 rounded-2xl font-medium hover:bg-neutral-200 transition-colors">Cancel</button>
                            <button onClick={confirmModal.onConfirm} className="flex-1 py-3 bg-red-500 text-white rounded-2xl font-medium shadow-md shadow-red-500/20 hover:bg-red-600 transition-colors">Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
